import React, { useMemo, useState } from 'react'
import {
  Badge,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  UncontrolledTooltip
} from 'reactstrap'
import moment from 'moment'
import PropTypes from 'prop-types'

import { useTranslation } from 'react-i18next'

// Import steps in order of execution (same as index.jsx)
const IMPORT_STEPS = [
  'refreshing_source_code',
  'parsing_translation_files',
  'detecting_locales',
  'detecting_string_locales',
  'updating_existing_strings',
  'destroying_removed_strings',
  'creating_strings',
  'creating_strings_with_unknown_locale',
  'importing_values_from_other_locales',
  'detecting_unused_strings',
  'detecting_duplicated_strings'
]

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '-'
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  if (seconds < 60) return `${seconds.toFixed(2)}s`
  return moment.duration(seconds * 1000).humanize()
}

const formatDiff = (currentDuration, previousDuration) => {
  if (!currentDuration || !previousDuration) return null

  const diff = currentDuration - previousDuration
  const percentChange = ((diff / previousDuration) * 100).toFixed(0)

  if (Math.abs(diff) < 0.01) return null // Ignore tiny differences

  return {
    diff,
    percentChange,
    isFaster: diff < 0
  }
}

const StepTimingsModal = ({
  currentImport = null,
  isOpen,
  onClose,
  projectImports = []
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])

  // Find immediate previous import (next in the list since sorted by created_at desc)
  const currentIndex = projectImports?.findIndex(
    (imp) => imp.id === currentImport?.id
  ) ?? -1
  const importsLength = projectImports?.length ?? 0
  const defaultComparisonImport = currentIndex !== -1 && currentIndex < importsLength - 1
    ? projectImports[currentIndex + 1]
    : null

  const [comparisonImportId, setComparisonImportId] = useState(
    defaultComparisonImport?.id || ''
  )

  // Available imports for comparison (all except current, with completed timings)
  const availableComparisons = useMemo(() => {
    if (!projectImports) return []
    return projectImports.filter((imp) => (
      imp.id !== currentImport?.id && imp.state !== 'importing'
    ))
  }, [projectImports, currentImport])

  const comparisonImport = useMemo(() => (
    availableComparisons.find((imp) => imp.id === comparisonImportId) || null
  ), [availableComparisons, comparisonImportId])

  const currentTimings = currentImport?.step_timings || {}
  const comparisonTimings = comparisonImport?.step_timings || {}

  const formatImportLabel = (imp) => {
    const date = moment(imp.created_at).format('MMM D, HH:mm')
    const branch = imp.branch_name || 'unknown'
    return `${date} (${branch})`
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {t('ProjectImports.Index.StepTimingsModal.title')}
      </ModalHeader>
      <ModalBody>
        {availableComparisons.length > 0 && (
          <div className="mb-3">
            <label
              htmlFor="comparison-select"
              className="form-label text-muted small"
            >
              {t('ProjectImports.Index.StepTimingsModal.compareWith')}
            </label>
            <Input
              id="comparison-select"
              type="select"
              value={comparisonImportId}
              onChange={(e) => setComparisonImportId(e.target.value)}
              bsSize="sm"
            >
              <option value="">
                {t('ProjectImports.Index.StepTimingsModal.noComparison')}
              </option>
              {availableComparisons.map((imp) => (
                <option key={imp.id} value={imp.id}>
                  {formatImportLabel(imp)}
                </option>
              ))}
            </Input>
          </div>
        )}

        <div className="step-timings-list">
          {IMPORT_STEPS.map((step) => {
            const currentTiming = currentTimings[step]
            const comparisonTiming = comparisonTimings[step]
            const diffInfo = comparisonImport
              ? formatDiff(currentTiming?.duration, comparisonTiming?.duration)
              : null
            const isSkipped = !currentTiming
            const skipReasonKey = step === 'importing_values_from_other_locales'
              ? 'onlyFirstImport'
              : 'default'

            return (
              <div
                key={step}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div className="d-flex align-items-center">
                  <span className="me-2">
                    {t(`ProjectCard.SynchronizationStatus.${step}`).replace(' ...', '')}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <span
                    className="font-monospace"
                    style={{ minWidth: '80px', textAlign: 'right' }}
                  >
                    {isSkipped ? '—' : formatDuration(currentTiming?.duration)}
                  </span>

                  {isSkipped && (
                    <>
                      <Badge
                        id={`skip-${step}`}
                        color="secondary"
                        className="ms-2"
                        style={{ minWidth: '70px' }}
                      >
                        {t('ProjectImports.Index.StepTimingsModal.skipped')}
                      </Badge>
                      <UncontrolledTooltip placement="top" target={`skip-${step}`}>
                        {t(`ProjectImports.Index.StepTimingsModal.tooltips.${skipReasonKey}`)}
                      </UncontrolledTooltip>
                    </>
                  )}

                  {!isSkipped && diffInfo && (
                    <Badge
                      color={diffInfo.isFaster ? 'success' : 'danger'}
                      className="ms-2"
                      style={{ minWidth: '70px' }}
                    >
                      {diffInfo.isFaster ? '↓' : '↑'}
                      {' '}
                      {Math.abs(diffInfo.percentChange)}
                      %
                    </Badge>
                  )}
                  {!isSkipped && comparisonImport && !diffInfo && currentTiming?.duration && (
                    <Badge color="secondary" className="ms-2" style={{ minWidth: '70px' }}>
                      =
                    </Badge>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Total duration */}
        <div className="d-flex justify-content-between align-items-center py-3 mt-2 border-top border-2">
          <strong>{t('ProjectImports.Index.StepTimingsModal.total')}</strong>
          <div className="d-flex align-items-center">
            <strong
              className="font-monospace"
              style={{ minWidth: '80px', textAlign: 'right' }}
            >
              {formatDuration(currentImport?.import_stats?.duration)}
            </strong>
            {comparisonImport && (() => {
              const diffInfo = formatDiff(
                currentImport?.import_stats?.duration,
                comparisonImport?.import_stats?.duration
              )
              if (!diffInfo) return null
              return (
                <Badge
                  color={diffInfo.isFaster ? 'success' : 'danger'}
                  className="ms-2"
                  style={{ minWidth: '70px' }}
                >
                  {diffInfo.isFaster ? '↓' : '↑'}
                  {' '}
                  {Math.abs(diffInfo.percentChange)}
                  %
                </Badge>
              )
            })()}
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

StepTimingsModal.propTypes = {
  currentImport: PropTypes.shape({
    id: PropTypes.string.isRequired,
    import_stats: PropTypes.shape({
      duration: PropTypes.number
    }),
    step_timings: PropTypes.objectOf(PropTypes.shape({
      duration: PropTypes.number,
      end: PropTypes.string,
      start: PropTypes.string
    }))
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectImports: PropTypes.arrayOf(PropTypes.shape({
    branch_name: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    step_timings: PropTypes.objectOf(PropTypes.shape({
      duration: PropTypes.number
    }))
  }))
}

export default StepTimingsModal
